import React from 'react';
import { TableSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import IconButton from '../ui/IconButton';
import ColumnFilter from './ColumnFilter';
import './list.css';

/**
 * Dense list table — 40px header, 44px rows, checkbox column, hover-revealed actions.
 *
 * Column shape:
 *   { key, header, width?, sortable?, align?, mono?, render(row), headerTitle?,
 *     filter? }
 * `width` is a px number; columns without one share the remaining space.
 * `filter` is 'text' | 'number' | 'date' and puts a filter menu in the header.
 */
export default function DataTable({
  columns,
  rows,
  rowKey = (row) => row.id,
  loading = false,
  selectable = true,
  selectedIds,
  onToggleRow,
  onToggleAll,
  sort,
  onSort,
  columnFilters = {},
  onColumnFilter,
  onRowClick,
  onRowAction,
  empty = {},
  noun = 'records',
  footer,
}) {
  const ids = rows.map(rowKey);
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds?.has(id));
  const someSelected = ids.some((id) => selectedIds?.has(id)) && !allSelected;

  const showEmpty = !loading && rows.length === 0;

  return (
    <div className="sv-table-card">
      <div className="sv-table" role="table" aria-rowcount={rows.length}>
        <div className="sv-table__head" role="row">
          {selectable && (
            <div className="sv-table__cell sv-table__cell--check" role="columnheader">
              <input
                type="checkbox"
                className="sv-checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={() => onToggleAll?.(ids)}
                aria-label={allSelected ? 'Clear selection' : 'Select all rows'}
                disabled={ids.length === 0}
              />
            </div>
          )}

          {columns.map((col) => {
            const isSorted = sort?.column === col.key;
            const filterable = col.filter && onColumnFilter;
            // A column may render something the server filters under another name --
            // "Flow / credits" shows two values but filters on available_credits.
            const filterKey = col.filterKey || col.key;
            return (
              <div
                key={col.key}
                role="columnheader"
                aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                className={[
                  'sv-table__cell',
                  'sv-table__cell--head',
                  col.align === 'right' ? 'is-right' : '',
                  col.sortable ? 'is-sortable' : '',
                  isSorted ? 'is-sorted' : '',
                  filterable ? 'has-filter' : '',
                  columnFilters[filterKey] ? 'is-filtered' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ width: col.width, flex: col.width ? '0 0 auto' : 1 }}
                onClick={col.sortable ? () => onSort?.(col.key) : undefined}
                title={col.headerTitle}
              >
                <span>{col.header}</span>
                {col.sortable && isSorted && (
                  <i
                    className={`bx ${sort.direction === 'asc' ? 'bx-chevron-up' : 'bx-chevron-down'}`}
                    aria-hidden="true"
                  />
                )}
                {filterable && (
                  <ColumnFilter
                    column={filterKey}
                    kind={col.filter}
                    value={columnFilters[filterKey]}
                    onApply={onColumnFilter}
                  />
                )}
              </div>
            );
          })}

          {onRowAction && <div className="sv-table__cell sv-table__cell--actions" role="columnheader" />}
        </div>

        <div className="sv-table__body">
          {loading && <TableSkeleton columns={columns} selectable={selectable} />}

          {!loading &&
            rows.map((row) => {
              const id = rowKey(row);
              const isSelected = selectedIds?.has(id);
              return (
                <div
                  key={id}
                  role="row"
                  tabIndex={onRowClick ? 0 : -1}
                  className={`sv-table__row ${isSelected ? 'is-selected' : ''} ${
                    onRowClick ? 'is-clickable' : ''
                  }`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {selectable && (
                    <div
                      className="sv-table__cell sv-table__cell--check"
                      role="cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="sv-checkbox"
                        checked={!!isSelected}
                        onChange={() => onToggleRow?.(id)}
                        aria-label="Select row"
                      />
                    </div>
                  )}

                  {columns.map((col) => (
                    <div
                      key={col.key}
                      role="cell"
                      className={[
                        'sv-table__cell',
                        col.align === 'right' ? 'is-right' : '',
                        col.mono ? 'sv-mono' : '',
                        col.muted ? 'is-muted' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ width: col.width, flex: col.width ? '0 0 auto' : 1 }}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </div>
                  ))}

                  {onRowAction && (
                    <div
                      className="sv-table__cell sv-table__cell--actions"
                      role="cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        icon="bx-dots-horizontal-rounded"
                        size="sm"
                        label="Row actions"
                        className="sv-row-action"
                        onClick={(e) => onRowAction(row, e)}
                      />
                    </div>
                  )}
                </div>
              );
            })}

          {showEmpty && (
            <EmptyState
              icon={empty.icon}
              title={empty.title || `No ${noun} found`}
              description={empty.description}
              actionLabel={empty.actionLabel}
              actionIcon={empty.actionIcon}
              onAction={empty.onAction}
            />
          )}
        </div>

        {footer}
      </div>
    </div>
  );
}
